import React, { useState } from 'react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

const QUANTITY_OPTIONS = [1, 2, 3, 4, 5, 6];

interface SystemAccordionListProps<T extends { id: string }> {
  sectionLabel: string;
  systems: T[];
  onSystemsChange: React.Dispatch<React.SetStateAction<T[]>>;
  createDefaultSystem: () => T;
  onSystemRemove?: (id: string) => void;
  renderFields: (values: T, index: number, onChange: (field: keyof T, value: any) => void) => React.ReactNode;
}

function SystemAccordionList<T extends { id: string }>({
  sectionLabel,
  systems,
  onSystemsChange,
  createDefaultSystem,
  onSystemRemove,
  renderFields,
}: SystemAccordionListProps<T>) {
  const [removedCache, setRemovedCache] = useState<Map<number, T>>(new Map());
  const [pendingQuantity, setPendingQuantity] = useState<number | null>(null);
  const [expanded, setExpanded] = useState<number>(-1);

  const applyReduce = (newQuantity: number) => {
    const updatedCache = new Map(removedCache);
    for (let i = newQuantity; i < systems.length; i++) {
      onSystemRemove?.(systems[i].id);
      updatedCache.set(i, systems[i]);
    }
    setRemovedCache(updatedCache);
    onSystemsChange((prev) => prev.slice(0, newQuantity));
    if (expanded >= newQuantity) setExpanded(newQuantity - 1);
  };

  const handleQuantityChange = (newQuantity: number) => {
    const currentQuantity = systems.length;

    if (newQuantity > currentQuantity) {
      const newSystems = [...systems];
      for (let i = currentQuantity; i < newQuantity; i++) {
        const cached = removedCache.get(i);
        newSystems.push(cached ?? createDefaultSystem());
      }
      const updatedCache = new Map(removedCache);
      for (let i = currentQuantity; i < newQuantity; i++) {
        updatedCache.delete(i);
      }
      setRemovedCache(updatedCache);
      onSystemsChange(newSystems);
    } else if (newQuantity < currentQuantity) {
      const hasData = systems.slice(newQuantity).some((sys) =>
        Object.entries(sys as Record<string, unknown>)
          .filter(([key]) => key !== 'id')
          .some(([, v]) => v !== '' && v !== false && v !== undefined && v !== null),
      );

      if (hasData) {
        setPendingQuantity(newQuantity);
      } else {
        applyReduce(newQuantity);
      }
    }
  };

  const handleFieldChange = (index: number, field: keyof T, value: any) => {
    onSystemsChange((prev) => {
      const updated = [...prev];
      updated[index] = { ...(updated[index] as T), [field]: value };
      return updated;
    });
  };

  return (
    <Box>
      <Box sx={{ mb: 2 }}>
        <FormControl variant="filled" sx={{ minWidth: 200 }}>
          <InputLabel>Number of {sectionLabel} Systems</InputLabel>
          <Select value={systems.length} onChange={(e) => handleQuantityChange(Number(e.target.value))}>
            {QUANTITY_OPTIONS.map((n) => (
              <MenuItem key={n} value={n}>
                {n}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {removedCache.size > 0 && (
          <Typography variant="caption" sx={{ ml: 2, color: 'text.secondary' }}>
            {removedCache.size} hidden system(s) cached
          </Typography>
        )}
      </Box>

      {systems.map((sys, idx) => (
        <Accordion
          key={sys.id}
          expanded={expanded === idx}
          onChange={(_, isExpanded) => setExpanded(isExpanded ? idx : -1)}
          sx={{ mb: 1 }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography sx={{ fontWeight: 600, color: '#3d5a80' }}>
              {sectionLabel} System {idx + 1}
              {(sys as any).location ? ` — ${(sys as any).location}` : ''}
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            {renderFields(sys, idx, (field, value) => handleFieldChange(idx, field, value))}
          </AccordionDetails>
        </Accordion>
      ))}

      <Dialog open={pendingQuantity !== null} onClose={() => setPendingQuantity(null)}>
        <DialogTitle>Remove Systems?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Reducing from {systems.length} to {pendingQuantity} will hide system(s) with entered data. The data will be
            cached while you continue editing. It will not be submitted when you save.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingQuantity(null)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => {
              if (pendingQuantity !== null) {
                applyReduce(pendingQuantity);
                setPendingQuantity(null);
              }
            }}
          >
            Continue
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default SystemAccordionList;
